import { useAppSelector } from "../hooks";

/**
 * One site's entry in `user.permission[]`, as the user screens write it
 * (`WebsitePermissionFormContainer`: read-only, read & edit, read & edit &
 * publish). The server stores the array untyped (`models/user.ts`,
 * `permission: { type: Array }`), so this is the client's own contract for it.
 * Every field is optional: the hook below returns a frozen `{}` when there is
 * no permission for the active site.
 */
export interface SitePermission {
  siteId?: string;
  siteDomain?: string;
  CountryCode?: string;
  read?: boolean;
  edit?: boolean;
  publish?: boolean;
}

/**
 * Shared, single frozen fallback so every empty/`null` read returns the SAME
 * object reference — putting the fallback here (not `|| {}` inside a selector)
 * is what stops a fresh `{}` per render from churning `useSelector`.
 */
const EMPTY: Readonly<SitePermission> = Object.freeze({});

/**
 * Shared read of the current site permission (SCRUM-1021 follow-up, the
 * useImageUpload gold-standard style).
 *
 * `state.auth.current_permission` is `""` initially, a permission object once a
 * site's permissions load, or `null` when the signed-in user has no permission
 * for the active site (SCRUM-856). ~34 containers read it directly, in three
 * inconsistent fallback styles:
 *   - no fallback                  → `null.publish` can throw in the gating checks
 *   - `|| {}` OUTSIDE the selector  → a fresh `{}` every falsy render (re-render churn)
 *   - `?. || {}` INSIDE the selector
 *
 * This hook collapses all three into one read that normalises the empty/`null`
 * value to a single frozen `EMPTY` sentinel. The `|| EMPTY` sits OUTSIDE the
 * selector so `useSelector` still compares the stable store value (no new object
 * per render). Contract: tiny, returns a primitive object — the caller keeps ALL
 * permission gating (`permission.edit` / `.publish` checks).
 *
 * @returns the current permission, or a frozen `{}` when none
 */
export function useCurrentPermission(): Readonly<SitePermission> {
  // The auth slice is still JavaScript, so its initial `""` types the field as
  // `string`; the cast names the three values the reducer actually stores.
  const current = useAppSelector((s) => s.auth?.current_permission) as
    | SitePermission
    | ""
    | null;
  return current || EMPTY;
}
